/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * "Every interaction" (design reference: the Customer 360 aside): the latest
 * activities and Echo posts on the account as one timeline. Read-only; the
 * Activity tab holds the full lists with their add, edit and delete actions.
 */

import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import { CalendarCheck, CheckSquare, Mail, MessageSquare, Phone, Users, History } from "lucide-react";
import { v2 as t, v2Tones } from "styles/tokens/v2";
import { relativeTime } from "./format";

const TYPE_STYLE = {
  call: { icon: Phone, tone: v2Tones.success },
  meeting: { icon: Users, tone: v2Tones.purple },
  email: { icon: Mail, tone: v2Tones.warning },
  task: { icon: CheckSquare, tone: v2Tones.info },
  echo: { icon: MessageSquare, tone: v2Tones.neutral },
};

const stripHtml = (html) => String(html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

/** Activities and feed items as timeline entries, newest first. */
export function timelineEntries(activities = [], feedItems = [], limit = 8) {
  const entries = [];
  activities.filter(Boolean).forEach((a) => {
    const kind = String(a.type || "task").toLowerCase();
    entries.push({
      id: `a-${a.id}`,
      kind: TYPE_STYLE[kind] ? kind : "task",
      title: a.title || a.type || "Activity",
      meta: [a.type, a.status, a.assigned_to_name].filter(Boolean).join(" · "),
      at: a.due_date || a.created_at,
    });
  });
  feedItems.filter(Boolean).forEach((f) => {
    entries.push({
      id: `f-${f.id}`,
      kind: "echo",
      title: stripHtml(f.body).slice(0, 120) || "Message",
      meta: f.created_by_name || f.created_by_user?.fullname || "Echo",
      at: f.created_at,
    });
  });
  return entries
    .filter((e) => e.at)
    .sort((x, y) => new Date(y.at) - new Date(x.at))
    .slice(0, limit);
}

export default function ActivityTimeline({ activities, feedItems, onOpenAll, emptyText }) {
  const entries = timelineEntries(activities, feedItems);
  return (
    <Card sx={{ p: 0, overflow: "hidden" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2.25, py: 1.75, borderBottom: `1px solid ${t.border}` }}>
        <History size={17} color={t.text2} aria-hidden="true" />
        <Typography component="h2" sx={{ fontSize: 15, fontWeight: 600, color: t.text, flex: 1 }}>
          Every interaction
        </Typography>
        {onOpenAll && (
          <ButtonBase onClick={onOpenAll} sx={{ fontFamily: "inherit", fontSize: 13, fontWeight: 600, color: t.link, borderRadius: "6px", px: 0.75, py: 0.25, "&:hover": { textDecoration: "underline" } }}>
            View all
          </ButtonBase>
        )}
      </Box>
      {entries.length === 0 ? (
        <Box sx={{ py: 4, px: 2, textAlign: "center" }}>
          <Box sx={{ width: 44, height: 44, mx: "auto", mb: 1.25, borderRadius: "50%", display: "grid", placeItems: "center", bgcolor: t.surface2, color: t.text3 }}>
            <CalendarCheck size={20} aria-hidden="true" />
          </Box>
          <Typography sx={{ fontSize: 14, color: t.text2 }}>{emptyText}</Typography>
        </Box>
      ) : (
        <Box component="ol" sx={{ listStyle: "none", m: 0, px: 2.25, py: 1.75 }}>
          {entries.map((e, i) => {
            const { icon: Icon, tone } = TYPE_STYLE[e.kind];
            const last = i === entries.length - 1;
            return (
              <Box component="li" key={e.id} sx={{ position: "relative", display: "flex", gap: 1.5, pb: last ? 0 : 2 }}>
                {!last && <Box aria-hidden="true" sx={{ position: "absolute", left: 14, top: 30, bottom: 2, width: "1px", bgcolor: t.border }} />}
                <Box sx={{ width: 29, height: 29, flexShrink: 0, borderRadius: "50%", display: "grid", placeItems: "center", bgcolor: tone.bg, color: tone.fg, boxShadow: `0 0 0 3px ${t.surface}` }}>
                  <Icon size={14} aria-hidden="true" />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1, pt: 0.25 }}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "baseline" }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: t.text, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {e.title}
                    </Typography>
                    <Typography component="time" dateTime={e.at} sx={{ fontSize: 12, color: t.text3, whiteSpace: "nowrap" }}>
                      {relativeTime(e.at)}
                    </Typography>
                  </Box>
                  {e.meta && <Typography sx={{ fontSize: 12.5, color: t.text2, mt: 0.25 }}>{e.meta}</Typography>}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Card>
  );
}

ActivityTimeline.propTypes = {
  activities: PropTypes.arrayOf(PropTypes.object),
  feedItems: PropTypes.arrayOf(PropTypes.object),
  onOpenAll: PropTypes.func,
  emptyText: PropTypes.string,
};

ActivityTimeline.defaultProps = { activities: [], feedItems: [], onOpenAll: null, emptyText: "No activity on this account yet." };
